import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Search, X } from "lucide-react";

export default function SearchBar({ onFilterChange }) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [country, setCountry] = useState("");
  const [categories, setCategories] = useState([]);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/config").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);

  // Whenever category changes, notify the parent so the grid can filter
  // itself to only highlight matching brands. Same for country + query, but
  // the primary UX ask from the user is category-first.
  useEffect(() => {
    onFilterChange?.(category);
  }, [category, onFilterChange]);

  const run = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.get("/pixels/search", {
        params: { q: q || undefined, category: category || undefined, country: country || undefined, limit: 30 },
      });
      setResults(data.results || []);
    } finally { setBusy(false); }
  };

  const clear = () => { setQ(""); setCategory(""); setCountry(""); setResults(null); onFilterChange?.(""); };

  return (
    <div data-testid="search-bar" className="max-w-3xl mx-auto">
      <form onSubmit={run} className="glass p-2 flex items-center gap-2 flex-wrap sm:flex-nowrap">
        <Search className="w-4 h-4 ml-3 text-[var(--fg-muted)] shrink-0" />
        <input
          data-testid="search-input"
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search brands, industries, tags…"
          className="grow bg-transparent outline-none px-1 py-2 text-sm min-w-[140px]"
        />
        <select
          data-testid="search-category" value={category} onChange={(e) => setCategory(e.target.value)}
          className="input-lux max-w-[150px] py-2 text-sm rounded-full"
        >
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          data-testid="search-country" value={country} onChange={(e) => setCountry(e.target.value)}
          placeholder="Country" className="input-lux max-w-[120px] py-2 text-sm rounded-full"
        />
        <button data-testid="search-submit" className="btn-primary" disabled={busy}>{busy ? "…" : "Search"}</button>
        {(q || category || country || results) && (
          <button type="button" onClick={clear} data-testid="search-clear" className="btn-ghost p-2">
            <X className="w-4 h-4" />
          </button>
        )}
      </form>

      {results && (
        <div className="mt-4 space-y-2" data-testid="search-results">
          {results.length === 0 && (
            <div className="text-sm text-[var(--fg-muted)] text-center py-4">
              No advertisers match — try a different query.
            </div>
          )}
          {results.map((b) => (
            <a
              key={b.id}
              href={b.destination_url || "#"}
              target="_blank" rel="noopener noreferrer"
              onClick={() => http.post(`/pixels/${b.id}/click`).catch(() => {})}
              data-testid={`result-${b.id}`}
              className="card-elev p-3 flex gap-3 items-center hover:-translate-y-0.5 transition"
            >
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--bg)] border border-[var(--line-strong)] flex items-center justify-center shrink-0">
                {b.image_url ? <img src={b.image_url} alt="" className="w-full h-full object-cover" />
                             : <div className="w-full h-full bg-gradient-to-br from-violet-500 to-cyan-400" />}
              </div>
              <div className="min-w-0 grow">
                <div className="font-semibold truncate">{b.brand_name || "Advertiser"}</div>
                <div className="text-xs text-[var(--fg-muted)] truncate">
                  {b.hover_slogan || `${b.category || "—"} · ${b.country || "🌍"}`}
                </div>
              </div>
              <span className="pill">{b.tier}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
